import React from 'react';
import { Loader2, Sparkles, Users, Target, Clock, TrendingUp, AlertTriangle, IndianRupee, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { jobsApi, getErrorMessage } from '../../services/api';
import { formatINR } from '../command-center/constants';
import { useCanSeeRevenue } from '../../lib/perms';
import { toast } from 'sonner';

const STAGE_COLOR = {
  sourced: 'bg-slate-200',
  screening: 'bg-sky-300',
  client_submission: 'bg-violet-300',
  interview_scheduled: 'bg-amber-300',
  interview_selected: 'bg-emerald-300',
  offer_released: 'bg-teal-400',
  joined: 'bg-green-500',
  rejected: 'bg-red-300',
  dropped: 'bg-orange-300',
  dropped_out: 'bg-rose-500',
};

const KpiTile = ({ icon: Icon, label, value, accent = 'text-slate-700', testid }) => (
  <div
    className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
      {Icon ? <Icon className="w-3 h-3" /> : null}
      {label}
    </div>
    <div className={`text-lg font-bold mt-0.5 ${accent}`}>{value}</div>
  </div>
);

const JobSummaryDrawer = ({ open, onOpenChange, jobId }) => {
  const canSeeRevenue = useCanSeeRevenue();
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiNarrative, setAiNarrative] = React.useState('');

  const loadSummary = React.useCallback(async (id) => {
    setLoading(true);
    setData(null);
    setAiNarrative('');
    try {
      const res = await jobsApi.summary(id);
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load job summary'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open && jobId) loadSummary(jobId);
  }, [open, jobId, loadSummary]);

  const handleGenerateAi = async () => {
    if (!jobId) return;
    setAiLoading(true);
    try {
      const res = await jobsApi.aiSummary(jobId);
      setAiNarrative(res.data?.narrative || '');
      toast.success('AI insight generated');
    } catch (err) {
      toast.error(getErrorMessage(err, 'AI generation failed'));
    } finally {
      setAiLoading(false);
    }
  };

  const job = data?.job;
  const k = data?.kpis || {};
  const funnel = data?.funnel || [];
  const maxFunnel = Math.max(1, ...funnel.map((f) => f.count));
  const topRecs = data?.top_recruiters || [];
  const stuck = data?.stuck_candidates || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto p-0"
        data-testid="job-summary-drawer"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <SheetHeader className="space-y-1">
              <SheetTitle className="text-xl font-bold text-slate-900 truncate" data-testid="job-summary-title">
                {job?.title || (loading ? 'Loading…' : 'Job Summary')}
              </SheetTitle>
              <SheetDescription className="text-xs text-slate-500">
                {job ? `${job.client_name || 'Internal'} · ${job.location || '—'}` : 'Pipeline analysis'}
              </SheetDescription>
            </SheetHeader>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            data-testid="job-summary-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading summary…
            </div>
          ) : !data ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              No data available for this job yet.
            </div>
          ) : (
            <>
              {/* AI Narrative */}
              <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-4" data-testid="job-ai-block">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-violet-900">
                    <Sparkles className="w-4 h-4" />
                    AI Insight
                  </div>
                  <Button
                    size="sm"
                    variant={aiNarrative ? 'outline' : 'default'}
                    onClick={handleGenerateAi}
                    disabled={aiLoading}
                    className={aiNarrative ? '' : 'bg-violet-700 hover:bg-violet-800'}
                    data-testid="job-ai-generate-btn"
                  >
                    {aiLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Thinking…</>
                    ) : aiNarrative ? 'Regenerate' : 'Generate'}
                  </Button>
                </div>
                <div className="mt-2 text-sm text-slate-700 leading-relaxed min-h-[3rem]" data-testid="job-ai-narrative">
                  {aiNarrative || (
                    <span className="text-slate-400 italic text-xs">
                      Click "Generate" for a 3-4 sentence executive summary on progress, risks and next actions.
                    </span>
                  )}
                </div>
              </div>

              {/* KPI tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="job-kpi-grid">
                <KpiTile
                  icon={Users}
                  label="Pipeline"
                  value={k.total_pipeline ?? 0}
                  accent="text-slate-900"
                  testid="kpi-pipeline"
                />
                <KpiTile
                  icon={Target}
                  label="Fill rate"
                  value={`${k.fill_rate_pct ?? 0}%`}
                  accent={k.fill_rate_pct >= 100 ? 'text-emerald-700' : k.fill_rate_pct >= 50 ? 'text-amber-700' : 'text-rose-700'}
                  testid="kpi-fill-rate"
                />
                <KpiTile
                  icon={TrendingUp}
                  label="Conversion"
                  value={`${k.conversion_pct ?? 0}%`}
                  accent="text-violet-700"
                  testid="kpi-conversion"
                />
                <KpiTile
                  icon={Clock}
                  label="Avg TAT"
                  value={k.avg_tat_days != null ? `${k.avg_tat_days}d` : '—'}
                  accent="text-teal-700"
                  testid="kpi-tat"
                />
                <KpiTile
                  icon={Users}
                  label="Joined"
                  value={k.joined ?? 0}
                  accent="text-emerald-700"
                  testid="kpi-joined"
                />
                <KpiTile
                  icon={Target}
                  label="Open vacancies"
                  value={k.open_vacancies ?? 0}
                  accent={k.open_vacancies > 0 ? 'text-amber-700' : 'text-emerald-700'}
                  testid="kpi-vacancies"
                />
                <KpiTile
                  icon={Clock}
                  label="Days open"
                  value={k.days_open ?? 0}
                  accent={k.days_open >= 30 ? 'text-rose-700' : 'text-slate-900'}
                  testid="kpi-days-open"
                />
                {canSeeRevenue && (
                  <KpiTile
                    icon={IndianRupee}
                    label="Joined CTC"
                    value={k.joined_ctc_total ? formatINR(k.joined_ctc_total) : '—'}
                    accent="text-rose-700"
                    testid="kpi-joined-ctc"
                  />
                )}
              </div>

              {/* Offer → Join TAT chips — two flavours when manual offer
                  letter dates have been recorded:
                    • Manual : real contractual benchmark (offer_letter_date → joining)
                    • Auto   : recruiter activity benchmark (stage_change → joining)
                  Falls back to a single chip if only one signal is available. */}
              {(k.avg_offer_to_join_manual_days != null || k.avg_offer_to_join_auto_days != null) && (
                <div
                  className="rounded-xl border border-slate-200 p-3 bg-gradient-to-r from-slate-50/60 to-white"
                  data-testid="offer-to-join-tat"
                >
                  <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-teal-600" />
                    Offer → Join TAT
                    <span className="text-[10px] font-normal text-slate-400 ml-1">
                      How fast offers turn into joinings
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {k.avg_offer_to_join_manual_days != null && (
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-teal-50 border border-teal-200"
                        data-testid="otj-manual"
                        title="Average days from offer-letter-date to joining-date — the contractual benchmark"
                      >
                        <span className="text-[10px] uppercase tracking-wide text-teal-700 font-bold">Real</span>
                        <span className="text-base font-bold text-teal-900">{k.avg_offer_to_join_manual_days}d</span>
                        <span className="text-[10px] text-teal-700/70">avg · {k.offer_to_join_manual_count} offer{k.offer_to_join_manual_count === 1 ? '' : 's'}</span>
                      </div>
                    )}
                    {k.avg_offer_to_join_auto_days != null && (
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200"
                        data-testid="otj-auto"
                        title="Average days from when recruiter moved stage to Offer Released to joining"
                      >
                        <span className="text-[10px] uppercase tracking-wide text-slate-600 font-bold">Activity</span>
                        <span className="text-base font-bold text-slate-800">{k.avg_offer_to_join_auto_days}d</span>
                        <span className="text-[10px] text-slate-500">avg · {k.offer_to_join_auto_count} offer{k.offer_to_join_auto_count === 1 ? '' : 's'}</span>
                      </div>
                    )}
                    {/* Helpful delta hint when both numbers exist */}
                    {k.avg_offer_to_join_manual_days != null && k.avg_offer_to_join_auto_days != null && (
                      <div className="flex items-center text-[10px] text-slate-500 italic">
                        Real {k.avg_offer_to_join_manual_days > k.avg_offer_to_join_auto_days ? 'lags' : 'leads'} activity by{' '}
                        <span className="ml-1 font-semibold text-slate-700">
                          {Math.abs(k.avg_offer_to_join_manual_days - k.avg_offer_to_join_auto_days).toFixed(1)}d
                        </span>
                      </div>
                    )}
                  </div>
                  {k.avg_offer_to_join_manual_days == null && k.avg_offer_to_join_auto_days != null && (
                    <div className="text-[10px] text-slate-400 italic mt-1.5">
                      Tip: enter the actual offer letter date on a few joined candidates (pencil icon on Application History) to surface the real benchmark.
                    </div>
                  )}
                </div>
              )}

              {/* Funnel */}
              <div className="rounded-xl border border-slate-200 p-4" data-testid="job-funnel">
                <div className="text-sm font-semibold text-slate-700 mb-3">Pipeline Funnel</div>
                <div className="space-y-1.5">
                  {funnel.map((f) => (
                    <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-row-${f.stage}`}>
                      <div className="w-28 text-xs text-slate-600 flex-shrink-0">{f.label}</div>
                      <div className="flex-1 bg-slate-100 rounded h-5 overflow-hidden relative">
                        <div
                          className={`h-full ${STAGE_COLOR[f.stage] || 'bg-slate-300'} transition-all`}
                          style={{ width: `${(f.count / maxFunnel) * 100}%` }}
                        />
                      </div>
                      <div className="w-10 text-right text-xs font-semibold text-slate-700 flex-shrink-0" data-testid={`funnel-count-${f.stage}`}>
                        {f.count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top recruiters */}
              {topRecs.length > 0 && (
                <div className="rounded-xl border border-slate-200 p-4" data-testid="job-top-recruiters">
                  <div className="text-sm font-semibold text-slate-700 mb-3">Top recruiters on this job</div>
                  <div className="space-y-2">
                    {topRecs.map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-sm">
                        <div className="text-slate-700 truncate">{r.name}</div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="outline" className="text-[10px]">
                            {r.submissions} submissions
                          </Badge>
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">
                            {r.joined} joined
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Drop reasons aggregation — surfaces leakage patterns */}
              {(data.drop_reasons || []).length > 0 ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4" data-testid="job-drop-reasons">
                  <div className="text-sm font-semibold text-rose-900 mb-2">
                    Why candidates dropped
                  </div>
                  <div className="space-y-1.5">
                    {(data.drop_reasons || []).map((r) => (
                      <div key={r.reason} className="flex items-center justify-between text-xs">
                        <span className="text-slate-700">{r.label}</span>
                        <Badge variant="outline" className="text-[10px] bg-white">
                          {r.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Stuck candidates */}
              {stuck.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="job-stuck">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Stuck candidates ({data.stuck_count})
                  </div>
                  <div className="text-xs text-amber-700 space-y-1">
                    {stuck.slice(0, 6).map((s) => (
                      <div key={s.application_id} className="flex justify-between">
                        <span>{s.stage.replace(/_/g, ' ')}</span>
                        <span className="font-mono">{s.days_in_stage}d in stage</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default JobSummaryDrawer;
